/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	sourcesHeading: {
		id: 'ai-mate.conversation-assistant.split-layout.default-right-panel.sources-heading-non-final',
		defaultMessage: 'Sources',
		description: 'Heading for the list of sources used to generate the chat result.',
	},
	sourcesPlaceholder: {
		id: 'ai-mate.conversation-assistant.split-layout.default-right-panel.sources-placeholder-non-final',
		defaultMessage: 'Sources used by the assistant will appear here.',
		description: 'Placeholder text shown in the Sources section before any sources are available.',
	},
	outputHeading: {
		id: 'ai-mate.conversation-assistant.split-layout.default-right-panel.output-heading-non-final',
		defaultMessage: 'Output',
		description: 'Heading for the list of output artefacts produced by the chat result.',
	},
	outputPlaceholder: {
		id: 'ai-mate.conversation-assistant.split-layout.default-right-panel.output-placeholder-non-final',
		defaultMessage: 'Artefacts generated by the assistant will appear here.',
		description: 'Placeholder text shown in the Output section before any artefacts are available.',
	},
});

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		height: '100%',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		overflowY: 'auto',
		backgroundColor: token('elevation.surface'),
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

export const RightPanel = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div data-testid="conversation-assistant-split-layout-right-panel" css={styles.root}>
			<section css={styles.section} aria-label={formatMessage(messages.sourcesHeading)}>
				<Heading size="xsmall">{formatMessage(messages.sourcesHeading)}</Heading>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.sourcesPlaceholder)}
				</Text>
			</section>
			<section css={styles.section} aria-label={formatMessage(messages.outputHeading)}>
				<Heading size="xsmall">{formatMessage(messages.outputHeading)}</Heading>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.outputPlaceholder)}
				</Text>
			</section>
		</div>
	);
};
